import React, { useState, useEffect } from 'react';
import { StepIndicator } from '../ui/StepIndicator';
import { ClientSearchStep } from './ClientSearchStep';
import { ServiceSelectionStep } from './ServiceSelectionStep';
import { BeneficiaryStep } from './BeneficiaryStep';
import { SummaryStep } from './SummaryStep';
import { bookingWizardService } from '../../services/bookingWizardService';
import { WizardData, Professional } from '../../types/bookingWizard.types';
import api from '../../services/api';
import { formatLocalDate } from '../../utils/formatters';
import './AdminBookingWizard.css';

interface AdminBookingWizardProps {
    professionalId: number;
    date: Date;
    time: string;
    onSuccess: () => void;
    onCancel: () => void;
}

export const AdminBookingWizard: React.FC<AdminBookingWizardProps> = ({
    professionalId,
    date,
    time,
    onSuccess,
    onCancel
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [professional, setProfessional] = useState<Professional | null>(null);
    const [wizardData, setWizardData] = useState<WizardData>({
        client: null,
        professional: null,
        service: null,
        date: formatLocalDate(date),
        time: time,
        isForDependent: false,
        dependent: null,
        newDependentName: ''
    });

    const totalSteps = 4;

    useEffect(() => {
        loadProfessional();
    }, [professionalId]);

    const loadProfessional = async () => {
        try {
            const response = await api.get(`/professionals/${professionalId}/`);
            setProfessional(response.data);
            setWizardData(prev => ({ ...prev, professional: response.data }));
        } catch (err) {
            console.error('Erro ao carregar profissional:', err);
        }
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleStepClick = (targetStep: number) => {
        if (targetStep < currentStep) {
            setCurrentStep(targetStep);
        }
    };

    const handleUpdateData = (newData: Partial<WizardData>) => {
        setWizardData(prev => ({ ...prev, ...newData }));
    };

    const handleConfirm = async () => {
        if (!wizardData.client || !wizardData.service) {
            setError('Dados incompletos. Verifique cliente e serviço.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const startDateTime = new Date(`${wizardData.date}T${wizardData.time}:00`);

            // Calculate end_time based on service duration
            // Duration format: "HH:MM:SS" from Django DurationField
            const durationParts = wizardData.service.duration.split(':');
            const durationHours = parseInt(durationParts[0]) || 0;
            const durationMinutes = parseInt(durationParts[1]) || 0;
            const durationSeconds = parseInt(durationParts[2]) || 0;

            const endDateTime = new Date(startDateTime);
            endDateTime.setHours(endDateTime.getHours() + durationHours);
            endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);
            endDateTime.setSeconds(endDateTime.getSeconds() + durationSeconds);

            const customerName = wizardData.isForDependent
                ? (wizardData.dependent?.name || wizardData.newDependentName)
                : wizardData.client.name;

            const payload = {
                client: wizardData.client.id,
                professional: professionalId,
                service: wizardData.service.id,
                start_time: startDateTime.toISOString(),
                end_time: endDateTime.toISOString(),
                customer_name: customerName,
                customer_phone: wizardData.client.phone,
                dependent: wizardData.isForDependent ? wizardData.dependent?.id : null,
                notes: ''
            };

            await bookingWizardService.createAppointment(payload);
            onSuccess();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = 'Erro ao criar agendamento';
            
            if (errorData?.non_field_errors && errorData.non_field_errors.length > 0) {
                errorMessage = errorData.non_field_errors[0];
            } else if (errorData?.error) {
                errorMessage = errorData.error;
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            } else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
                // Format field errors
                const fieldErrors = Object.entries(errorData)
                    .map(([field, errors]) => {
                        if (Array.isArray(errors)) {
                            return `${field}: ${errors.join(', ')}`;
                        }
                        return `${field}: ${errors}`;
                    })
                    .join('; ');
                errorMessage = fieldErrors;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <ClientSearchStep
                        data={wizardData}
                        onUpdate={handleUpdateData}
                        onNext={handleNext}
                    />
                );
            case 2:
                return (
                    <ServiceSelectionStep
                        professionalId={professionalId}
                        data={wizardData}
                        onUpdate={handleUpdateData}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );
            case 3:
                return (
                    <BeneficiaryStep
                        data={wizardData}
                        onUpdate={handleUpdateData}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );
            case 4:
                return (
                    <SummaryStep
                        data={wizardData}
                        professionalId={professionalId}
                        onConfirm={handleConfirm}
                        onBack={handleBack}
                        loading={loading}
                        error={error}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="admin-booking-wizard">
            <div className="wizard-header">
                <h2>Novo Agendamento</h2>
                <button className="close-btn" onClick={onCancel}>×</button>
            </div>

            <StepIndicator
                steps={[
                    { label: 'Cliente' },
                    { label: 'Serviço' },
                    { label: 'Beneficiário' },
                    { label: 'Resumo' }
                ]}
                currentStep={currentStep}
                onStepClick={handleStepClick}
                showLabels={true}
                completedIcon="checkmark"
            />

            <div className="wizard-content">
                {renderStep()}
            </div>
        </div>
    );
};

export default AdminBookingWizard;
